/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import * as MsDyn365 from '@msdyn365-commerce/core';
import {
    FullProductInput,
    getFullProducts,
    getProductDetailsCriteriaFromActionInput,
    getProductUrlSync
} from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';
import { readAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CustomersDataActions.g';
import { IAAAOrderConfirmationItemViewProps, RequestOrderLine } from './components';
import { IAaaOrderConfirmationData } from './aaa_order_confirmation.data';
import { IHeadingData, IAaa_order_confirmationProps } from './aaa_order_confirmation.props.autogenerated';
import { getRequestOrderB2CAsync } from '../../dataActions/DataActionExtension.g';
import { IActionContext } from '@msdyn365-commerce/core';

export interface IAAAOrderConfirmationViewProps extends IAaa_order_confirmationProps<IAaaOrderConfirmationData> {
    RequestOrderLines: IModuleProps;
    heading?: React.ReactNode;
    Products?: INodeProps;
    Product?: INodeProps;
    ProductDetails?: INodeProps;
    ProductDimensions?: INodeProps;
    products?: IAAAOrderConfirmationItemViewProps[];
    telemetryContent?: ITelemetryContent;
    orderConfirmationHeading?: string;
    orderConfirmationContent?: string;
    requestOrderId?: string;
}

/**
 *
 * WishlistItems component.
 * @extends {React.PureComponent<IAaa_order_confirmationProps<IAaa_order_confirmationData>, IWishlistItemActionMessageState>}
 */
@observer
class RequestOrderLines extends React.Component<IAaa_order_confirmationProps<IAaaOrderConfirmationData>, any> {
    private readonly telemetryContent: ITelemetryContent;

    constructor(props: IAaa_order_confirmationProps<IAaaOrderConfirmationData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.state = {
            requestOrderLines: [],
            jewelerName: '',
            requestOrderId: ''
        };
    }

    public async componentDidMount(): Promise<void> {
        let jewelerName;
        let requestOrderId: any = '';
        const params = new URLSearchParams(this.props.context.request.url.requestUrl.search);
        if (params.has('requestOrderId')) {
            // check url has recordId...
            requestOrderId = params.get('requestOrderId');
        }
        const customerInfo = this.props.data.customerInformation.result;
        if (customerInfo) {
            jewelerName = await this.getStockistCustomers(customerInfo.AccountNumber);
        }

        const requestOrderLines = await this.getRequestOrderLines(requestOrderId, this.props.context.actionContext);
        this.setState({ requestOrderLines: requestOrderLines, jewelerName, requestOrderId });
    }
    private readonly getStockistCustomers = async (customerAccountNumber: any) => {
        if (customerAccountNumber) {
            const jewelerInfo: any = await readAsync({ callerContext: this.props.context.actionContext }, customerAccountNumber);
            if (!(jewelerInfo.Attributes.length > 0)) {
                if (this.props.context.actionContext.requestContext.user.signInUrl) {
                    window.location.href = this.props.context.actionContext.requestContext.user.signInUrl;
                }
            }
            const stockist = jewelerInfo.Attributes.find((attr: any) => {
                return attr.Name === this.props.config.stockistAttributeName;
            });
            if (stockist) {
                return stockist.AttributeValue.StringValue;
            }
        }
        return '#jwelerName';
    };
    public render(): JSX.Element | null {
        const { heading, className, orderConfirmationHeadingText, orderConfirmationContentText } = this.props.config;
        const customerInfo = this.props.data.customerInformation.result;
        let orderConfirmationHeading, orderConfirmationContent;
        if (customerInfo && customerInfo.Name) {
            orderConfirmationHeading = orderConfirmationHeadingText ? orderConfirmationHeadingText : '';
            orderConfirmationContent = orderConfirmationContentText ? orderConfirmationContentText : '';
            orderConfirmationHeading = orderConfirmationHeading.replace('#customerName', customerInfo.Name);
            orderConfirmationContent = orderConfirmationContent.replace('#jewelerName', this.state.jewelerName);
        }
        const viewProps: IAAAOrderConfirmationViewProps = {
            ...(this.props as IAaa_order_confirmationProps<IAaaOrderConfirmationData>),
            RequestOrderLines: {
                moduleProps: this.props,
                className: classnames('ms-wishlist-items', className)
            },
            heading: this._renderHeading(heading),
            Products: {
                className: 'ms-wishlist-items__products',
                tag: 'ul'
            },
            Product: {
                className: 'ms-wishlist-items__product',
                tag: 'li'
            },
            ProductDetails: {
                className: 'ms-wishlist-items__product-link',
                tag: 'a'
            },
            telemetryContent: this.telemetryContent,
            ProductDimensions: {
                className: 'ms-wishlist-items__product-dimensions'
            },
            products: this._renderProductList(),
            orderConfirmationHeading: orderConfirmationHeading,
            orderConfirmationContent: orderConfirmationContent,
            requestOrderId: this.state.requestOrderId
        };
        return this.state.requestOrderLines.length > 0 ? (this.props.renderView(viewProps) as React.ReactElement) : null;
    }

    private getRequestOrderLines = async (requestOrderId: string, ctx: IActionContext): Promise<[]> => {
        if (!requestOrderId) {
            throw new Error('[getActiveWishlistItems]No valid Input was provided, failing');
        }

        if (ctx.requestContext.user.isAuthenticated) {
            //
            let requestOrderLines: any = await getRequestOrderB2CAsync({ callerContext: ctx, queryResultSettings: {} }, requestOrderId);
            requestOrderLines = requestOrderLines ? requestOrderLines.RequestOrder : [];
            if (requestOrderLines && requestOrderLines.length > 0) {
                const productInputs: FullProductInput[] = [];
                requestOrderLines.forEach((requestOrderLine: any) => {
                    if (requestOrderLine.ProductId) {
                        productInputs.push(
                            new FullProductInput(
                                requestOrderLine.ProductId,
                                ctx.requestContext.apiSettings,
                                getProductDetailsCriteriaFromActionInput(ctx)
                            )
                        );
                    }
                });

                if (productInputs.length > 0) {
                    return getFullProducts(productInputs, ctx)
                        .then(products => {
                            if (products) {
                                const mappedProducts: any = [];
                                requestOrderLines.forEach((requestOrderLine: any) => {
                                    if (requestOrderLine.ProductId) {
                                        const product: any = products.find((p: any) => {
                                            return requestOrderLine.ProductId === p.ProductDetails.RecordId;
                                        });
                                        if (product) {
                                            const productInfo: any = { ...product };
                                            // find snapshotid...
                                            if (requestOrderLine.Snapshotid) {
                                                // init into product..
                                                productInfo.snapShotId = requestOrderLine.Snapshotid;
                                            }
                                            // init into product..
                                            productInfo.b2cPrice = requestOrderLine.B2CPrice ? requestOrderLine.B2CPrice : 0;
                                            productInfo.b2bPrice = requestOrderLine.B2BPrice ? requestOrderLine.B2BPrice : 0;
                                            mappedProducts.push(productInfo);
                                        }
                                    }
                                });
                                return mappedProducts;
                            }
                            return [];
                        })
                        .catch(error => {
                            ctx.trace(error);
                            ctx.telemetry.error(error.message);
                            ctx.telemetry.debug('[getActiveWishlistItems]Unable to hydrate cart with product information');
                            throw new Error('[getActiveWishlistItems]Unable to hydrate cart with product information');
                        });
                }
                ctx.trace('No productIds found for wishlist');
            } else {
                ctx.trace('[getActiveWishlistItems]Not able to get wishlists for the customer');
                return [];
            }
        }
        ctx.trace('[getActiveWishlistItems]User token not found for wishlists');
        return [];
    };

    public handleHeadingChange = (event: MsDyn365.ContentEditableEvent) => (this.props.config.heading!.text = event.target.value);

    private _renderProductList(): IAAAOrderConfirmationItemViewProps[] {
        const {
            priceFree,
            originalPriceText,
            currentPriceText,
            outOfStockText,
            productDimensionTypeColor,
            productDimensionTypeSize,
            productDimensionTypeStyle,
            productDimensionTypeAmount
        } = this.props.resources;
        const { context, telemetry, data } = this.props;
        const { imageSettings, enableImageProductLink } = this.props.config;
        const customerInformationData: any = data.customerInformation && data.customerInformation.result;
        return this.state.requestOrderLines.map((product: any, index: any) => {
            const productPrice = { ...product.ProductPrice };
            if (product.snapShotId) {
                if (customerInformationData.IsB2b) {
                    productPrice.AdjustedPrice = product.b2bPrice;
                    productPrice.BasePrice = product.b2bPrice;
                    productPrice.CustomerContextualPrice = product.b2bPrice;
                    productPrice.TradeAgreementPrice = product.b2bPrice;
                } else {
                    productPrice.AdjustedPrice = product.b2cPrice;
                    productPrice.BasePrice = product.b2cPrice;
                    productPrice.CustomerContextualPrice = product.b2cPrice;
                    productPrice.TradeAgreementPrice = product.b2cPrice;
                }
            }
            const currentProductProps = {
                product: product.ProductDetails,
                price: productPrice,
                context,
                productUrl: getProductUrlSync(product.ProductDetails, context.actionContext, undefined),
                imageSettings,
                outOfStockText,
                index,
                itemKey: `${product.ProductDetails.RecordId}-${product.ProductDetails.ItemId}-${index}`,
                telemetry,
                moduleId: this.props.id,
                moduleTypeName: this.props.typeName,
                enableImageProductLink,
                telemetryContent: this.telemetryContent,
                resources: {
                    productDimensionTypeColor,
                    productDimensionTypeSize,
                    productDimensionTypeStyle: product.ProductDetails.IsGiftCard ? productDimensionTypeAmount : productDimensionTypeStyle,
                    freePriceText: priceFree,
                    originalPriceText,
                    currentPriceText
                },
                snapshotId: product.snapShotId,
                productsCount: this.state.requestOrderLines.length
            };

            return RequestOrderLine(currentProductProps)!;
        });
    }

    private readonly _renderHeading = (heading?: IHeadingData): JSX.Element | undefined => {
        if (!heading) {
            return undefined;
        }
        return (
            <MsDyn365.Text
                className='ms-wishlist-items__heading'
                tag={heading.tag || 'h2'}
                text={heading.text}
                editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
            />
        );
    };
}

export default RequestOrderLines;
